import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Autocomplete,
  TextField,
  IconButton,
  Container,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import apiService from "../../../../../ApiService";
import { API_ENDPOINTS } from "../../../../../ApiService";
import { MainContainer } from "../../../styles/sharedStyles";
import Title from "../../../../components/title/Title";
import Section from "../../../../components/section/Section";
import { tokens } from "../../../../../theme";

// Helper for pastel shading
const shadeColor = (color, percent) => {
  let num = parseInt(color.slice(1), 16);
  let amt = Math.round(2.55 * percent);
  let R = (num >> 16) + amt;
  let G = ((num >> 8) & 0x00ff) + amt;
  let B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const baseColors = {
  "Upper Body": "#ce93d8",
  "Lower Body": "#d4e157",
  Core: "#81D4FA",
  Other: "#E0E0E0",
};

// Define two alternating hues per region for better patterning
const regionColors = {
  // Mid-tone pink + deeper rose
  "Upper Body": ["#f48fb1", "#ec407a"],
  // Soft green + fresh mint
  "Lower Body": ["#aed581", "#66bb6a"],
  // Light blue + crisp cerulean
  Core: ["#64b5f6", "#42a5f5"],
  // Neutral light grays
  Other: ["#e0e0e0", "#bdbdbd"],
};

// Alternate between the two colors based on column index
const getHighlightColor = (region, index) => {
  const arr = regionColors[region] || regionColors.Other;
  return arr[index % arr.length];
};

const groupMusclesByBodyPart = (muscles) => {
  const order = ["Upper Body", "Lower Body", "Core", "Other"];
  const grouped = muscles.reduce((acc, m) => {
    const key = m.bodyPart || "Other";
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});
  return order.reduce((acc, key) => {
    if (grouped[key]) acc[key] = grouped[key];
    return acc;
  }, {});
};

const ExerciseIntensityPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [allExercises, setAllExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState([]);
  const [selectedMainFamilies, setSelectedMainFamilies] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);

  useEffect(() => {
    fetchMuscleGroups();
    fetchAllExercises();
  }, []);

  const fetchAllExercises = async () => {
    try {
      const resp = await apiService.get(
        API_ENDPOINTS.FITNESS_GET_ALL_EXERCISES_INTENSITY
      );
      const transformed = resp.data.map((ex) => ({
        ...ex,
        sets: ex.sets || 1,
        muscleIntensities: ex.intensities.reduce((acc, i) => {
          acc[i.muscleGroupId] = i.intensity;
          return acc;
        }, {}),
      }));
      setAllExercises(transformed);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMuscleGroups = async () => {
    try {
      const resp = await apiService.get(API_ENDPOINTS.FITNESS_GET_MUSCLE);
      setMuscleGroups(resp.data);
    } catch (e) {
      console.error(e);
    }
  };

  // --- New derived lists & filters ---
  const mainFamilies = useMemo(() => {
    const map = new Map();
    allExercises.forEach((ex) =>
      map.set(ex.mainFamily.mainFamilyId, ex.mainFamily)
    );
    return Array.from(map.values());
  }, [allExercises]);

  const filteredExercises = useMemo(
    () =>
      allExercises.filter((ex) => {
        if (selectedMuscleGroups.length) {
          const ok = selectedMuscleGroups.some(
            (mg) => ex.muscleIntensities[mg.id] > 0
          );
          if (!ok) return false;
        }
        if (selectedMainFamilies.length) {
          const ok = selectedMainFamilies.some(
            (mf) => mf.mainFamilyId === ex.mainFamily.mainFamilyId
          );
          if (!ok) return false;
        }
        return true;
      }),
    [allExercises, selectedMuscleGroups, selectedMainFamilies]
  );

  const groupedMuscles = useMemo(
    () => groupMusclesByBodyPart(muscleGroups),
    [muscleGroups]
  );
  const orderedMuscles = useMemo(
    () => Object.values(groupedMuscles).flat(),
    [groupedMuscles]
  );

  const muscleIndexMapping = useMemo(() => {
    const map = {};
    Object.entries(groupedMuscles).forEach(([region, arr]) => {
      arr.forEach((m, idx) => {
        map[m.id] = { region, index: idx };
      });
    });
    return map;
  }, [groupedMuscles]);

  const calculateTotals = (exs) => {
    return orderedMuscles.reduce((acc, m) => {
      acc[m.id] = exs.reduce(
        (sum, ex) => sum + (ex.muscleIntensities[m.id] || 0) * ex.sets,
        0
      );
      return acc;
    }, {});
  };

  const selectedTotals = useMemo(
    () => calculateTotals(selectedExercises),
    [selectedExercises, orderedMuscles]
  );

  return (
    <MainContainer>
      <Title
        title="Exercise Intensity"
        summary="Filter and pick exercises to build your heatmap"
      />

      {/* Enhanced Filter Panel */}
      <Section>
        <Paper
          elevation={2}
          sx={{ p: 2, mb: 3, backgroundColor: colors.grey[50] }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: colors.primary[600] }}
          >
            Filters
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Autocomplete
              multiple
              options={muscleGroups}
              getOptionLabel={(opt) => opt.name}
              value={selectedMuscleGroups}
              onChange={(_, v) => setSelectedMuscleGroups(v)}
              renderInput={(params) => (
                <TextField {...params} label="Muscle Groups" size="small" />
              )}
              sx={{ flex: 1, minWidth: 200 }}
            />

            <Autocomplete
              multiple
              options={mainFamilies}
              getOptionLabel={(opt) => opt.name}
              value={selectedMainFamilies}
              onChange={(_, v) => setSelectedMainFamilies(v)}
              renderInput={(params) => (
                <TextField {...params} label="Main Families" size="small" />
              )}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={filteredExercises.sort((a, b) =>
                a.exerciseName.localeCompare(b.exerciseName)
              )}
              getOptionLabel={(opt) => opt.exerciseName}
              value={selectedExercises}
              onChange={(_, v) => setSelectedExercises(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Exercises"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Box>
        </Paper>
      </Section>

      {selectedExercises.length > 0 && (
        <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ fontWeight: "bold" }}>
                    Exercise
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{ fontWeight: "bold" }}
                  >
                    Sets
                  </TableCell>
                  {Object.keys(groupedMuscles).map((region) => (
                    <TableCell
                      key={region}
                      colSpan={groupedMuscles[region].length}
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      {region}
                    </TableCell>
                  ))}
                  <TableCell rowSpan={2} align="center">
                    Remove
                  </TableCell>
                </TableRow>
                <TableRow>
                  {Object.entries(groupedMuscles).flatMap(([region, arr]) =>
                    arr.map((m, idx) => (
                      <TableCell key={m.id} align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            backgroundColor: getHighlightColor(region, idx),
                            p: "2px 4px",
                            borderRadius: "4px",
                            maxWidth: isDesktop ? 150 : 200,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {m.name}
                        </Typography>
                      </TableCell>
                    ))
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedExercises.map((ex) => (
                  <TableRow
                    key={ex.exerciseId}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {ex.exerciseName}
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={ex.sets}
                        onChange={(e) =>
                          setSelectedExercises((prev) =>
                            prev.map((p) =>
                              p.exerciseId === ex.exerciseId
                                ? { ...p, sets: Number(e.target.value) }
                                : p
                            )
                          )
                        }
                        inputProps={{
                          style: { textAlign: "center", width: 50 },
                        }}
                      />
                    </TableCell>
                    {orderedMuscles.map((m) => {
                      const val = (ex.muscleIntensities[m.id] || 0) * ex.sets;
                      const info = muscleIndexMapping[m.id] || {
                        region: "Other",
                        index: 0,
                      };
                      return (
                        <TableCell
                          key={m.id}
                          sx={{
                            backgroundColor: val
                              ? getHighlightColor(info.region, info.index)
                              : undefined,
                            border: "1px solid #ccc",
                          }}
                        >
                          {val ? val.toFixed(1) : ""}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setSelectedExercises((prev) =>
                            prev.filter((p) => p.exerciseId !== ex.exerciseId)
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell />
                  {orderedMuscles.map((m) => {
                    const total = selectedTotals[m.id] || 0;
                    const info = muscleIndexMapping[m.id] || {
                      region: "Other",
                      index: 0,
                    };
                    return (
                      <TableCell
                        key={m.id}
                        sx={{
                          backgroundColor: total
                            ? getHighlightColor(info.region, info.index)
                            : undefined,
                          border: "1px solid #ccc",
                        }}
                      >
                        {total.toFixed(1)}
                      </TableCell>
                    );
                  })}
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}
    </MainContainer>
  );
};

export default ExerciseIntensityPage;
